<template>
  <div class="new-project-provider">
    <div class="project-provider-header">
      <div class="start-date">
        <date-picker
          :inputDate="picker"
          :title="$t('start.date')"
          :isDateAllowed="allowedDates"
          @changed="startDateChanged"
        >
        </date-picker>
      </div>
      <div class="project-price">
        <div class="total-price-title">{{ $t('newProject.review.total.price') }}</div>
        <div class="total-price-value">$ {{ newProject.projectProviderData.projectTotalPrice }}</div>
      </div>
    </div>
    <div class="workers">
      <div v-for="worker in workers" :key="worker.id" class="worker">
        <new-project-worker
          :worker="worker"
          :selected="newProject.projectProviderData.serviceProviderId === worker.id"
          @detailed="displayWorkerDetails"
          @clicked="onWorkerChanged"
        >
        </new-project-worker>
      </div>
    </div>
    <worker-details-popup
      :visible-popup="showWorkerDetailsPopup"
      :worker="detailedWorker"
      @closed="onCloseWorkerDetailsButton"
    />
    <error-message :errorText="errorText" @close="errorText = ''" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment-business-days';

import { WORKING_HOURS_IN_DAY } from '@/constants/projects';
import store from '@/store';
import { LOAD_WORKERS, CLEAR_WORKERS } from '@/store/user/actionTypes';
import { estimateWebProject } from '@/services/projectCalculator';

import DatePicker from '@/components/common/DatePicker.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import WorkerDetailsPopup from '@/components/popup/WorkerDetailsPopup.vue';
import NewProjectWorker from '@/components/newProject/NewProjectWorker.vue';

export default {
  name: 'NewProjectProvider',
  components: {
    NewProjectWorker,
    WorkerDetailsPopup,
    DatePicker,
    ErrorMessage,
  },
  props: {
    newProject: Object,
  },
  data() {
    return {
      showWorkerDetailsPopup: false,
      detailedWorker: null,
      picker: moment().format('YYYY-MM-DD'),
      menu: false,
      estimation: 0,
      errorText: '',
    };
  },
  computed: {
    ...mapGetters(['workers']),
  },
  watch: {
    newProject: {
      deep: true,
      handler() {
        this.$emit('setRequiredDataForStep', 5);
      },
    },
  },
  async mounted() {
    this.refreshStartDatesEndDate(this.newProject.projectProviderData.startDate);
    if (this.newProject.workData.needToLoadWorkers) {
      await this.loadWorkers();
    }

    await this.checkEstimation();
  },
  methods: {
    onWorkerChanged(data) {
      this.newProject.projectProviderData.serviceProviderId = data.id;
      this.newProject.projectProviderData.serviceProviderName = data.name;
      const { hourRate } = this.workers.find(w => w.id === data.id);
      this.newProject.projectProviderData.projectTotalPrice =
        (hourRate || 0) * this.newProject.projectProviderData.projectDurationHours;
    },
    prepareStartAndEndDatesOfNewProject(date) {
      const startDate = date ? moment.utc(date).format() : moment.utc().startOf('day').businessAdd(1).format();
      const endDate = moment
        .utc(startDate)
        .businessAdd(Math.ceil(this.newProject.projectProviderData.projectDurationHours / WORKING_HOURS_IN_DAY))
        .format();

      return {
        startDate,
        endDate,
      };
    },
    displayWorkerDetails(worker) {
      this.detailedWorker = worker;
      this.showWorkerDetailsPopup = true;
    },
    onCloseWorkerDetailsButton() {
      this.showWorkerDetailsPopup = false;
      this.detailedWorker = null;
    },
    refreshStartDatesEndDate(date) {
      const dates = this.prepareStartAndEndDatesOfNewProject(date);
      this.newProject.projectProviderData.startDate = dates.startDate;
      this.picker = moment(this.newProject.projectProviderData.startDate).format('YYYY-MM-DD');
      this.newProject.projectProviderData.endDate = dates.endDate;
    },
    async refreshWorkers() {
      this.resetSelectedWorker();
      this.resetTotalPrice();
      store.dispatch(CLEAR_WORKERS);
      await this.loadWorkers();
    },
    resetTotalPrice() {
      this.newProject.projectProviderData.projectTotalPrice = 0;
    },
    async loadWorkers() {
      const start = this.newProject.projectProviderData.startDate;
      const end = this.newProject.projectProviderData.endDate;
      await store.dispatch(LOAD_WORKERS, { startDate: start, endDate: end });
      this.newProject.workData.needToLoadWorkers = false;
    },
    startDateChanged(data) {
      this.refreshStartDatesEndDate(data);
      this.refreshWorkers();
    },
    resetSelectedWorker() {
      this.newProject.projectProviderData.serviceProviderId = null;
      this.newProject.projectProviderData.serviceProviderName = null;
    },
    allowedDates(val) {
      const date = new Date(val);
      const day = date.getDay();
      return date > new Date() && day !== 0 && day !== 6;
    },
    async checkEstimation() {
      if (!this.newProject.projectProviderData.projectDurationHours) {
        await this.loadEstimation();
      }
    },
    async loadEstimation() {
      const bundle = this.newProject.projectBundlesData.data;
      const testingType = this.newProject.projectEnvironmentData.configuration.data;
      const { resources } = this.newProject.projectResourcesData;

      try {
        this.newProject.projectProviderData.projectDurationHours = await estimateWebProject(
          { bundle, testingType },
          resources,
        );
      } catch (e) {
        console.log(e);
        this.newProject.projectProviderData.projectDurationHours = 80;
      }
      if (!this.newProject.projectProviderData.projectDurationHours) {
        this.errorText = this.$t('newProject.estimation.failed');
      }
      this.refreshStartDatesEndDate(this.newProject.projectProviderData.startDate);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.new-project-provider {
  height: rem(716);

  .project-provider-header {
    .start-date {
      width: rem(200);
      padding-left: rem(22);
      display: inline-block;
    }

    .project-price {
      float: right;
      display: inline-block;
      padding-top: $space-4;
      padding-right: rem(25);

      .total-price-title {
        height: rem(10);
        color: var(--text-tertiary);
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: rem(10);
        text-transform: uppercase;
        text-align: right;
        margin-bottom: rem(5);
      }

      .total-price-value {
        height: rem(29);
        color: var(--text-secondary);
        font-size: rem(24);
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(29);
      }
    }
  }

  .workers {
    padding-left: rem(14);
    overflow-y: scroll;
    height: rem(630);

    .worker {
      display: inline-block;
      box-sizing: border-box;
      height: rem(115);
      width: rem(272);
      margin: $space-2;
    }
  }
}
</style>
